// src/components/stages/preconsult/TriageChipRow.tsx
// Known allergies / Current medications / Nurse notes as three equal
// pencil-to-edit chips — replaces PreConsultStage.tsx's old "Triage"
// SectionCard (now that Risk level/Pain scale live in PreConsultPatientHero,
// see that component) and its separate standalone "Nurse notes" SectionCard.
// See docs/HMS_V4_PRECONSULT_REDESIGN_PLAN.md Phase 1.3. Same triage state,
// same values submitted by completeStage — presentation only.
import { useState } from 'react';
import VoiceMicButton from '../../VoiceMicButton';
import { ALL_TRIAGE_FIELD_KEYS, type TriageFieldKey } from '../TriageFieldsEditor';
import { cn } from '../../../lib/cn';

export interface TriageChipRowValue {
  allergies: string;
  current_meds: string;
  nurse_notes: string;
}

interface ChipDef {
  key: keyof TriageChipRowValue;
  icon: string;
  label: string;
  placeholder: string;
  /** Which of TriageFieldsEditor's four keys gates this chip — absent for
   * Nurse notes, which (like before this component existed) has no
   * per-template opt-out. */
  triageKey?: TriageFieldKey;
  multiline?: boolean;
}

const CHIP_DEFS: ChipDef[] = [
  { key: 'allergies', icon: '🛡', label: 'Known allergies', placeholder: 'NKDA or list…', triageKey: 'allergies' },
  { key: 'current_meds', icon: '💊', label: 'Current medications', placeholder: 'List ongoing medications…', triageKey: 'current_meds' },
  { key: 'nurse_notes', icon: '🎙', label: 'Nurse notes (voice)', placeholder: 'Click to add voice note', multiline: true },
];

function Chip({ def, value, onChange, disabled }: {
  def: ChipDef;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);

  if (!expanded) {
    return (
      <button
        type="button"
        disabled={disabled}
        onClick={() => setExpanded(true)}
        className="rounded-[var(--radius)] border border-line bg-card p-2.5 text-left disabled:cursor-default"
      >
        <div className="flex items-center justify-between gap-2 text-[10.5px] font-bold text-ink-muted">
          <span>{def.icon} {def.label}</span>
          {!disabled && <span aria-hidden="true">✎</span>}
        </div>
        <div className={cn('mt-0.5 truncate text-[12.5px]', value ? 'text-ink' : 'italic text-ink-muted')}>
          {value || def.placeholder}
        </div>
      </button>
    );
  }

  return (
    <div className="rounded-[var(--radius)] border border-teal bg-card p-2.5">
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <label className="lbl flex items-center gap-1.5" style={{ marginBottom: 0 }}>
          <span>{def.icon} {def.label}</span>
          <VoiceMicButton onText={t => onChange((value ? value + ' ' : '') + t)} />
        </label>
        <button type="button" className="shrink-0 text-[11px] text-ink-muted hover:text-ink" onClick={() => setExpanded(false)}>✓ Done</button>
      </div>
      {def.multiline
        ? <textarea style={{ resize: 'vertical', height: 64 }} value={value} onChange={e => onChange(e.target.value)} placeholder={def.placeholder} />
        : <input value={value} onChange={e => onChange(e.target.value)} placeholder={def.placeholder} />}
    </div>
  );
}

export interface TriageChipRowProps {
  value: TriageChipRowValue;
  onChange: (key: keyof TriageChipRowValue, v: string) => void;
  /** Which of the four TriageFieldsEditor keys this ailment template wants —
   * null/absent means all four, same convention as TriageFieldsEditor's own
   * `enabledFields`. */
  enabledFields?: TriageFieldKey[] | null;
  disabled?: boolean;
}

export function TriageChipRow({ value, onChange, enabledFields, disabled }: TriageChipRowProps) {
  const enabled = new Set(enabledFields ?? ALL_TRIAGE_FIELD_KEYS);
  const visible = CHIP_DEFS.filter(def => !def.triageKey || enabled.has(def.triageKey));
  if (!visible.length) return null;
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      {visible.map(def => (
        <Chip key={def.key} def={def} value={value[def.key]} onChange={v => onChange(def.key, v)} disabled={disabled} />
      ))}
    </div>
  );
}
